import { useState } from 'react';
import { Plus, Pencil, Trash2, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useProductOrigins } from '@/hooks/warehouse/useProductOrigins';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import ProductOriginFormModal from './ProductOriginFormModal';

interface ProductOriginManagerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ProductOriginManagerModal({ open, onOpenChange }: ProductOriginManagerModalProps) {
  const [editingOrigin, setEditingOrigin] = useState<{ id: string; name: string } | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const {
    origins,
    deleteOrigin,
    reorderOrigin,
    isDeleting,
    isReordering,
  } = useProductOrigins();

  const handleCreate = () => {
    setEditingOrigin(null);
    setIsFormOpen(true);
  };

  const handleEdit = (origin: { id: string; name: string }) => {
    setEditingOrigin(origin);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteOrigin(id);
    } catch (error) {
      console.error('Error deleting origin:', error);
    }
  };

  const handleMoveUp = async (currentIndex: number) => {
    if (currentIndex === 0 || isReordering) return;

    const current = origins[currentIndex];
    const target = origins[currentIndex - 1];

    await reorderOrigin({
      originId: current.id,
      newSortOrder: target.sort_order,
      swapOriginId: target.id,
      swapSortOrder: current.sort_order,
    });
  };

  const handleMoveDown = async (currentIndex: number) => {
    if (currentIndex === origins.length - 1 || isReordering) return;

    const current = origins[currentIndex];
    const target = origins[currentIndex + 1];

    await reorderOrigin({
      originId: current.id,
      newSortOrder: target.sort_order,
      swapOriginId: target.id,
      swapSortOrder: current.sort_order,
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Gestionar Orígenes</DialogTitle>
            <DialogDescription>
              Administra los orígenes de productos. Puedes crear, editar, eliminar y reordenar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={handleCreate} size="sm" className="bg-brand-primary hover:bg-brand-primary-hover">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Origen
              </Button>
            </div>

            {/* Origins list */}
            <div className="border rounded-lg divide-y max-h-[60vh] overflow-y-auto">
              {origins.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  No hay orígenes configurados.
                </div>
              ) : (
                origins.map((origin, index) => (
                  <div
                    key={origin.id}
                    className="flex items-center gap-2 p-3 hover:bg-accent/50 group"
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground" />
                    <span className="flex-1 font-medium">{origin.name}</span>
                    <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0 || isReordering}
                        className="h-8 w-8"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === origins.length - 1 || isReordering}
                        className="h-8 w-8"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(origin)}
                        className="h-8 w-8"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar origen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              El origen "{origin.name}" se eliminará. Los productos
                              asociados deberán ser reasignados a otro origen.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(origin.id)}
                              disabled={isDeleting}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              {isDeleting ? 'Eliminando...' : 'Eliminar'}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ProductOriginFormModal
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        originId={editingOrigin?.id}
        initialName={editingOrigin?.name}
      />
    </>
  );
}
